"use client";

import { useState, useEffect, useMemo, memo, useCallback, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useDate } from '@/contexts/DateContext';
import { FaFacebook, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import { Phone, Mail, Home } from 'lucide-react';

const nepaliMonths = [
  'बैशाख', 'जेष्ठ', 'आषाढ', 'श्रावण', 'भाद्र', 'आश्विन',
  'कार्तिक', 'मंसिर', 'पौष', 'माघ', 'फाल्गुन', 'चैत्र'
];

function TopNav() {
  const [nepaliTime, setNepaliTime] = useState('00:00:00 AM');
  // Helper to convert English numerals and AM/PM to Devanagari
  const toDevanagariTime = (time: string): string => {
    const nepaliNumerals: { [key: string]: string } = {
      '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
      '5': '५', '6': '६', '7': '७', '8': '८', '9': '९'
    };
    // Extract hour and period
    const match = time.match(/(\d{2}):(\d{2}):(\d{2}) (AM|PM)/);
    let period = '';
    if (match) {
      let hour = parseInt(match[1], 10);
      const ampm = match[4];
      if (ampm === 'PM' && hour !== 12) hour += 12;
      if (ampm === 'AM' && hour === 12) hour = 0;
      // Morning: 4-11, Day: 11-16, Evening: 16-20, Night: 20-4
      if (hour >= 4 && hour < 11) {
        period = 'प्रातः'; // Morning
      } else if (hour >= 11 && hour < 16) {
        period = 'दिवा'; // Day
      } else if (hour >= 16 && hour < 20) {
        period = 'संध्या'; // Evening
      } else {
        period = 'रात्रि'; // Night
      }
    }
    // Replace numbers
    let result = time.replace(/[0-9]/g, d => nepaliNumerals[d] || d);
    // Remove AM/PM and add period
    result = result.replace(/ (AM|PM)/, ` ${period}`);
    return result;
  };
  const { dateData, loading: dateLoading } = useDate();
  const topNavRef = useRef<HTMLDivElement>(null);

  // Update every second with seconds and AM/PM format
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      setNepaliTime(toDevanagariTime(timeString));
    };

    updateTime();
    const intervalId = setInterval(updateTime, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Format Nepali date from context with error handling
  const nepaliDateDisplay = useMemo(() => {
    try {
      if (!dateData?.nepali) {
        return 'Loading...';
      }
      const { year, month, day, dayName } = dateData.nepali;
      
      // Validate date values
      if (!year || !month || !day) {
        return 'Loading...';
      }
      
      const monthName = nepaliMonths[month - 1] || '';
      // Convert numbers to Nepali numerals
      const toNepaliNumeral = (num: number): string => {
        try {
          const nepaliNumerals: { [key: string]: string } = {
            '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
            '5': '५', '6': '६', '7': '७', '8': '८', '9': '९'
          };
          return num.toString().split('').map(digit => nepaliNumerals[digit] || digit).join('');
        } catch {
          return num.toString();
        }
      };
      const nepaliYear = toNepaliNumeral(year);
      const nepaliDay = toNepaliNumeral(day);
      const weekDay = dayName ? `${dayName}, ` : '';
      return `${weekDay}${nepaliYear} ${monthName} ${nepaliDay}`;
    } catch (error) {
      console.error('Error formatting Nepali date:', error);
      return 'Loading...';
    }
  }, [dateData]);

  // Keep header offset synced to the real rendered top-nav height.
  useEffect(() => {
    const syncTopNavHeight = () => {
      const height = topNavRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty('--top-nav-height', `${height}px`);
    };

    // Deferred to the next frame so this initial read doesn't force a
    // synchronous layout flush mid-commit (see Header.tsx for the same fix).
    const raf = requestAnimationFrame(syncTopNavHeight);
    window.addEventListener('resize', syncTopNavHeight);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', syncTopNavHeight);
      document.documentElement.style.setProperty('--top-nav-height', '0px');
    };
  }, []);

  return (
    <div
      ref={topNavRef}
      className="fixed top-0 left-0 right-0 z-[110] text-white py-0.5 sm:py-1 w-full overflow-x-hidden bg-gradient-to-r from-[#8F1B1B] to-[#7a1515]"
      style={{
        backgroundColor: '#8F1B1B',
      }}
    >
      <div className="w-full px-4 lg:px-8 flex items-center justify-between">
        {/* Left: Nepali Flag, Date, Time — min-width shrinks on narrow phones so the
            social icons on the right never get pushed past the viewport edge, where
            overflow-x-hidden would silently clip them from view. */}
        <div className="flex flex-row items-center gap-1.5 sm:gap-2 min-w-0 sm:min-w-[220px] max-w-[420px] shrink">
          <div className="flex-shrink-0 relative w-5 h-5 sm:w-6 sm:h-6">
            <Image
              src="/nepal-flag.webp"
              alt="Nepal Flag"
              width={20}
              height={20}
              className="w-full h-full object-contain"
              unoptimized
              priority
            />
          </div>
          <span className="hidden sm:inline devanagari text-[11px] sm:text-sm font-semibold text-white tracking-wide" aria-hidden>
            नेपाली समय
          </span>
          <span className="devanagari text-sm sm:text-base md:text-lg font-bold text-white tabular-nums" title={`नेपाली समय: ${nepaliTime}`}>
            {nepaliTime}
          </span>
          <span className="hidden sm:inline devanagari text-[11px] sm:text-base md:text-lg font-semibold text-white truncate ml-2" title={`मिति: ${nepaliDateDisplay}`}>
            {nepaliDateDisplay}
          </span>
        </div>
        {/* Center: Home button, perfectly centered */}
        <div className="flex-1 flex items-center justify-center">
          <Link href="/" aria-label="Home" className="font-bold text-lg text-white hover:text-accent-400 transition-colors px-4 py-2 rounded">
            <Home className="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>
        {/* Right: Contact and social icons */}
        <div className="flex items-center gap-2">

          {/* Mobile: 2-column grid — contact left, socials right */}
          <div className="flex md:hidden items-center gap-2">
            {/* Contact column */}
            <div className="flex flex-col gap-0.5">
              <a href="tel:+9779851112728" className="p-2 rounded hover:bg-white/10 flex items-center justify-center" title="Call" aria-label="Call">
                <Phone className="w-3.5 h-3.5 text-white" />
              </a>
              <a href="mailto:shantiswasti9@gmail.com" className="p-2 rounded hover:bg-white/10 flex items-center justify-center" title="Email" aria-label="Email">
                <Mail className="w-3.5 h-3.5 text-white" />
              </a>
            </div>
            {/* Divider */}
            <div className="w-px h-8 bg-white/20" />
            {/* Social icons — 2×2 grid */}
            <div className="grid grid-cols-2 gap-0.5">
              <a href="https://www.facebook.com/astroswastishanti/" target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-white/10 flex items-center justify-center" title="Facebook" aria-label="Facebook">
                <FaFacebook className="w-3.5 h-3.5 text-white" />
              </a>
              <a href="https://www.instagram.com/astro_swastishanti/" target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-white/10 flex items-center justify-center" title="Instagram" aria-label="Instagram">
                <FaInstagram className="w-3.5 h-3.5 text-white" />
              </a>
              <a href="https://youtube.com/channel/UCp3XRzKfPoDWgPGsOsHqQyA" target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-white/10 flex items-center justify-center" title="YouTube" aria-label="YouTube">
                <FaYoutube className="w-3.5 h-3.5 text-white" />
              </a>
              <a href="https://www.tiktok.com/@swasti.shanti" target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-white/10 flex items-center justify-center" title="TikTok" aria-label="TikTok">
                <FaTiktok className="w-3.5 h-3.5 text-white" />
              </a>
            </div>
          </div>

          {/* Desktop: inline row */}
          <a href="tel:+9779851112728" className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/10 whitespace-nowrap" title="+9779851112728" aria-label="+9779851112728">
            <Phone className="w-4 h-4 text-white flex-shrink-0" />
            <span className="text-sm font-medium text-white">+9779851112728</span>
          </a>
          <a href="mailto:shantiswasti9@gmail.com" className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/10 whitespace-nowrap" title="shantiswasti9@gmail.com" aria-label="shantiswasti9@gmail.com">
            <Mail className="w-4 h-4 text-white flex-shrink-0" />
            <span className="text-sm font-medium text-white">shantiswasti9@gmail.com</span>
          </a>
          <div className="hidden md:flex items-center gap-1">
            <a href="https://www.facebook.com/astroswastishanti/" target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-white/10" title="Facebook" aria-label="Facebook">
              <FaFacebook className="w-4 h-4 text-white" />
            </a>
            <a href="https://www.instagram.com/astro_swastishanti/" target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-white/10" title="Instagram" aria-label="Instagram">
              <FaInstagram className="w-4 h-4 text-white" />
            </a>
            <a href="https://youtube.com/channel/UCp3XRzKfPoDWgPGsOsHqQyA" target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-white/10" title="YouTube" aria-label="YouTube">
              <FaYoutube className="w-4 h-4 text-white" />
            </a>
            <a href="https://www.tiktok.com/@swasti.shanti" target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-white/10" title="TikTok" aria-label="TikTok">
              <FaTiktok className="w-4 h-4 text-white" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Memoize TopNav to prevent unnecessary re-renders
export default memo(TopNav);
